/**
 * TweakableExposedToggle — options button for endpoint state.
 *
 * For param endpoints: off, tweakable, input, exposed
 * For non-param endpoints: off, input, exposed
 */

import { useCallback, useMemo } from 'react';
import { useRuntime } from '@/ui/contexts/RuntimeProvider';
import { endpointPath } from '@/runtime/util';
import { backfillSnapshots } from '@/nodes/PresetManager';
import { jukeboxController, webRuntimeApi } from '@/runtime/web';
import { OptionsButton, type Option, type Toggle } from '@/ui/shared/OptionsButton';
import { getEndpointColor } from '@/ui/shared/utils';
import type { EndpointState } from '@/nodes/Endpoint';

type State = 'off' | 'tweakable' | 'input' | 'exposed';

const ICON_SIZE = 14;
const SW = 2.5;

const iconRenderers: Record<State, (c: string) => React.ReactNode> = {
  off: (c) => (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={SW}>
      <circle cx="12" cy="12" r="4" />
    </svg>
  ),
  tweakable: (c) => (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  ),
  input: (c) => (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  ),
  exposed: (c) => (
    <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill={c} stroke={c} strokeWidth={SW}>
      <rect x="4" y="4" width="16" height="16" rx="2" />
    </svg>
  ),
  // Previous "upload" arrow icon — kept for potential reuse
  // exposedArrow: (c) => (
  //   <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round">
  //     <path d="M3 9V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4" />
  //     <polyline points="7 15 12 10 17 15" />
  //     <line x1="12" y1="10" x2="12" y2="21" />
  //   </svg>
  // ),
};

const COLORS: Record<State, string> = {
  off: '#4b5563',
  tweakable: '#f59e0b',
  input: '#3b82f6',
  exposed: '#ef4444',
};

const LABELS: Record<State, string> = {
  off: 'Default',
  tweakable: 'Tweakable',
  input: 'Input',
  exposed: 'External',
};

const TOOLTIPS: Record<State, string> = {
  off: '',
  tweakable: 'Include in performance snapshots',
  input: '',
  exposed: 'Expose on instance',
};

function buildOptions(isParam: boolean, readonly: boolean, handleColor: string): Option<State>[] {
  const states: State[] = isParam && !readonly
    ? ['exposed', 'tweakable', 'input', 'off']
    : ['exposed', 'input', 'off'];
  return states.map(s => {
    const color = s === 'input' ? handleColor : COLORS[s];
    return {
      value: s,
      label: LABELS[s],
      color,
      icon: iconRenderers[s](color),
      tooltip: TOOLTIPS[s],
    };
  });
}

function getCanonical(endpoint: EndpointState, isParam: boolean): State {
  if (endpoint.isExposed) return 'exposed';
  if (endpoint.isInput) return 'input';
  if (isParam && endpoint.tweakable) return 'tweakable';
  return 'off';
}

interface Props {
  instanceId: string;
  nodeId: string;
  endpoint: EndpointState;
  children?: React.ReactNode;
}

const checkbox = (checked: boolean, c: string) => (
  <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="3" />
    {checked && <polyline points="9 12 11 14 15 10" />}
  </svg>
);

export function TweakableExposedToggle({ instanceId, nodeId, endpoint, children }: Props) {
  const { setEndpointMetadata, exposeEndpoint, unexposeEndpoint, getValue } = useRuntime();

  const isParam = endpoint.dataFlow === 'param';
  const readonly = !!endpoint.readonly;
  const handleColor = getEndpointColor(endpoint.dataType, endpoint.dataFlow);
  const canonical = getCanonical(endpoint, isParam);
  const options = useMemo(() => buildOptions(isParam, readonly, handleColor), [isParam, readonly, handleColor]);

  const toggles = useMemo<Toggle[]>(() => {
    if (!endpoint.allowOutput) return [];
    return [{
      key: 'isOutput',
      label: 'Output',
      color: handleColor,
      icon: checkbox(endpoint.isOutput, endpoint.isOutput ? handleColor : '#4b5563'),
      checked: endpoint.isOutput,
      tooltip: '',
    }];
  }, [endpoint.allowOutput, endpoint.isOutput, handleColor]);

  const handleToggle = useCallback((key: string, checked: boolean) => {
    if (key === 'isOutput') {
      setEndpointMetadata(instanceId, nodeId, endpoint.id, 'isOutput', checked);
    }
  }, [instanceId, nodeId, endpoint.id, setEndpointMetadata]);

  const handleChange = useCallback((next: State) => {
    if (next === canonical) return;
    const path = endpointPath(nodeId, endpoint.id);

    // Undo current state
    if (canonical === 'exposed') unexposeEndpoint(instanceId, path);
    if (canonical === 'tweakable') setEndpointMetadata(instanceId, nodeId, endpoint.id, 'tweakable', false);
    if (canonical === 'input') setEndpointMetadata(instanceId, nodeId, endpoint.id, 'isInput', false);

    // Apply new state
    if (next === 'tweakable') {
      setEndpointMetadata(instanceId, nodeId, endpoint.id, 'tweakable', true);
      const currentValue = getValue(instanceId, nodeId, endpoint.id);
      const defId = webRuntimeApi.getInstanceDefinitionId(instanceId);
      backfillSnapshots(jukeboxController.snapshots, defId, nodeId, endpoint.id, currentValue);
    } else if (next === 'input') {
      setEndpointMetadata(instanceId, nodeId, endpoint.id, 'isInput', true);
    } else if (next === 'exposed') {
      exposeEndpoint(instanceId, path);
    }
  }, [canonical, instanceId, nodeId, endpoint.id, setEndpointMetadata, exposeEndpoint, unexposeEndpoint, getValue]);

  return (
    <OptionsButton
      options={options}
      value={canonical}
      onChange={handleChange}
      toggles={toggles}
      onToggle={handleToggle}
      title="Endpoint options"
    >
      {children}
    </OptionsButton>
  );
}
